
import React from 'react';
import { Message, Language } from '../../types';
import { TRANSLATIONS } from '../../constants';
import Visualizer from '../Visualizer';

interface LiveModeUIProps {
  isLiveActive: boolean;
  setIsLiveActive?: (active: boolean) => void;
  isRecognitionActive: boolean;
  isLoading: boolean;
  lastMessage?: Message;
  language: Language;
  onStartLive: () => void;
}

const LiveModeUI: React.FC<LiveModeUIProps> = ({
  isLiveActive,
  setIsLiveActive,
  isRecognitionActive,
  isLoading,
  lastMessage,
  language,
  onStartLive
}) => {
  const t = TRANSLATIONS[language];
  const isCurrentlyListening = isRecognitionActive && !isLoading;
  const isSpeaking = isLoading; // In this mock context, loading usually implies processing/speaking prep

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 relative overflow-hidden bg-white dark:bg-[#08080a] transition-all duration-700">
      
      {/* Background Ambient Glow */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-emerald-500/10 to-blue-500/10 rounded-full blur-[120px] transition-all duration-1000 ${isCurrentlyListening ? 'scale-110 opacity-60' : 'scale-100 opacity-30'}`}></div>
      </div>

      <div className="relative z-10 w-full max-w-xl flex flex-col items-center">
        
        {/* Main Mic Interaction Area */}
        <div onClick={() => setIsLiveActive?.(!isLiveActive)} className="relative group cursor-pointer">
            {/* Ripple Effects */}
            {isCurrentlyListening && (
                <>
                    <div className="absolute inset-0 rounded-full border border-emerald-500/30 animate-[ping_2s_linear_infinite]"></div>
                    <div className="absolute inset-[-20px] rounded-full border border-emerald-500/10 animate-[ping_3s_linear_infinite_0.5s]"></div>
                </>
            )}
            
            <div className={`
                relative w-32 h-32 lg:w-40 lg:h-40 rounded-[3rem] flex items-center justify-center transition-all duration-500 shadow-2xl
                ${isLiveActive 
                    ? (isCurrentlyListening 
                        ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/40 scale-110' 
                        : (isLoading 
                            ? 'bg-gradient-to-br from-blue-400 to-indigo-600 animate-pulse scale-105' 
                            : 'bg-white dark:bg-[#1a1a20] border-4 border-emerald-500/20'))
                    : 'bg-slate-100 dark:bg-white/5 grayscale opacity-50'}
            `}>
                <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-microphone'} text-4xl lg:text-5xl transition-colors duration-300 ${isLiveActive && (isCurrentlyListening || isLoading) ? 'text-white' : 'text-slate-400'}`}></i>
            </div>
        </div>

        {/* Status Text */}
        <div className="mt-12 text-center space-y-2 h-16">
            {isLiveActive ? (
                <>
                    <h2 className="text-xl lg:text-2xl font-black text-slate-800 dark:text-white transition-all">
                        {isCurrentlyListening ? t.listening : (isLoading ? t.analyzing : t.clickToStart)}
                    </h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest animate-pulse">
                        {isCurrentlyListening ? 'Listening...' : (isLoading ? 'Thinking...' : 'Ready')}
                    </p>
                </>
            ) : (
                <p className="text-sm font-bold text-slate-400">Tap microphone to start</p>
            )}
        </div>

        {/* Visualizer Area */}
        <div className="w-full h-24 mt-8 flex items-center justify-center">
            {isLiveActive ? (
                <Visualizer isActive={isCurrentlyListening || isLoading} color={isLoading ? '#6366f1' : '#10b981'} />
            ) : (
                <div className="w-full h-1 bg-slate-100 dark:bg-white/5 rounded-full"></div>
            )}
        </div>

        {/* Start Button (If Inactive) */}
        {!isLiveActive && (
            <button onClick={onStartLive} className="mt-8 px-10 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black text-xs shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3">
                <i className="fas fa-power-off"></i>
                {t.startLive}
            </button>
        )}
      </div>
    </div>
  );
};

export default LiveModeUI;
